import { get } from 'lodash';
import { ArrowUpRight, Plus, Settings } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TOPIC_DESCRIPTION_MAX_LENGTH,
  TOPIC_NAME_MAX_LENGTH,
} from '@/Domain/api/Conversation/ConversationTopics.constants';
import { TopicDto } from '@/Domain/api/Conversation/dto/Topic.dto';
import { TopicForm } from '@/Domain/components/Insights/Topics/TopicForm/TopicsForm';
import { TopicsGraphics } from '@/Domain/components/Insights/Topics/Topics.graphics';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useConversationTopicCreate } from '@/Domain/hooks/Conversation/useConversationTopicCreate.hook';

type Props = {
  topics: TopicDto[];
};

const MAX_TOPICS = 12;
export const MangeTopicsPage = ({ topics }: Props) => {
  const { t } = useTranslation('insights_page');

  const { handleCreateTopic, isPending } = useConversationTopicCreate();

  const [draftTopic, setDraftTopic] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);

  const isFull = topics.length >= MAX_TOPICS;
  const remaining = Math.max(0, MAX_TOPICS - topics.length);
  const canSubmit = !!draftTopic.trim() && !isFull && !isPending;

  const handleAdd = async () => {
    if (!canSubmit) return;
    setDraftError(null);
    try {
      await handleCreateTopic({
        topic: draftTopic.trim(),
        topic_description: draftDescription.trim(),
      });
      setDraftTopic('');
      setDraftDescription('');
      setDraftError(null);
    } catch (error) {
      const serverMessage = get(error, 'response.data.errors.root[0].message');
      setDraftError(
        typeof serverMessage === 'string' && serverMessage
          ? serverMessage
          : t('manage_topics.add_error'),
      );
    }
  };

  return (
    <div className="py-6 space-y-6">
      <div className="rounded-xl bg-muted">
        <div className="grid grid-cols-[1fr_auto] gap-20">
          <div className="py-6 pl-6 pr-6 xl:pr-0 flex flex-col justify-between gap-1">
            <h3 className="font-semibold flex items-center gap-2">
              <Settings className="size-4 bg-foreground-subtle rounded-full text-white p-1 text-sm box-content" />
              {t('manage_topics.title')}
            </h3>
            <p className="text-sm text-muted-foreground">{t('manage_topics.description')}</p>
            <a
              href="https://www.quickchat.ai/post/feature-announcement-topics"
              className="text-sm font-medium text-foreground flex items-center gap-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('manage_topics.learn_more')}
              <ArrowUpRight className="size-4" />
            </a>
          </div>
          <section className="hidden xl:block pr-10 mt-auto">
            <TopicsGraphics />
          </section>
        </div>
      </div>

      <TooltipProvider>
        <div>
          <section className="grid grid-cols-[0.5fr_2fr_4fr_0.5fr] gap-x-2 py-3 px-4 text-foreground-subtle bg-muted border-b mb-4 text-sm font-medium">
            <span>{t('manage_topics.form.header.no')}</span>
            <span className="pl-1">{t('manage_topics.form.header.topic_name')}</span>
            <span className="pl-2.5">{t('manage_topics.form.header.description')}</span>
          </section>

          {topics.map((topic, index) => (
            <TopicForm
              key={topic.id}
              topic={topic}
              index={index}
              topicLength={topics.filter(({ topic }) => !!topic.trim()).length}
            />
          ))}

          {!isFull && (
            <section
              data-testid="topic-draft-row"
              className="grid grid-cols-[0.5fr_2fr_4fr_0.5fr] gap-x-2 items-start border-t pt-4 mt-2"
            >
              <p className="mt-2 text-foreground-subtle text-sm whitespace-nowrap">
                {t('manage_topics.form.topic', { index: topics.length + 1 })}
              </p>
              <Input
                placeholder={t('manage_topics.form.topic_placeholder')}
                size="sm"
                value={draftTopic}
                onChange={(e) => {
                  setDraftTopic(e.target.value);
                  setDraftError(null);
                }}
                maxLength={TOPIC_NAME_MAX_LENGTH}
                aria-label={t('manage_topics.form.header.topic_name')}
                data-testid="topic-draft-name"
              />
              <AutosizeTextarea
                maxLength={TOPIC_DESCRIPTION_MAX_LENGTH}
                autoComplete="off"
                className="rounded-xl !min-h-[34px] !px-4 pb-2 pt-[9px] leading-none break-words"
                placeholder={t('manage_topics.form.description_placeholder')}
                minHeight={12}
                value={draftDescription}
                onChange={(e) => {
                  setDraftDescription(e.target.value);
                  setDraftError(null);
                }}
                aria-label={t('manage_topics.form.header.description')}
                data-testid="topic-draft-description"
              />
              <section className="flex gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="secondary"
                      size="icon"
                      aria-label={t('tooltip.add_topic')}
                      disabled={!canSubmit}
                      onClick={handleAdd}
                      data-testid="topic-add-button"
                    >
                      <Plus className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent align="end">{t('tooltip.add_topic')}</TooltipContent>
                </Tooltip>
              </section>
            </section>
          )}

          {draftError && (
            <p className="text-sm text-destructive mt-2" role="alert">
              {draftError}
            </p>
          )}

          <Separator className="mt-4" />
          <div
            className={`flex items-center gap-4 mt-4 ${isFull ? 'justify-between' : 'justify-end'}`}
          >
            {isFull && (
              <p className="text-sm text-muted-foreground" data-testid="topic-limit-message">
                {t('tooltip.all_topic_used', { maxTopics: MAX_TOPICS })}
              </p>
            )}
            <p className="text-sm text-foreground-subtle flex items-center">
              {t('manage_topics.remaining_topics')}
              <span className="font-medium ml-1">{remaining}</span>
            </p>
          </div>
        </div>
      </TooltipProvider>
    </div>
  );
};
